import z from 'zod';

import { getElementTypes } from '@@plate/utils/getElementTypes';

import { PlateBluesky, UnityBluesky } from './elements/Bluesky';
import { Caption } from './elements/Caption';
import { CaptionTitle } from './elements/CaptionTitle';
import { Credit } from './elements/Credit';
import {
    PlateCrossheadDefault,
    PlateCrossheadOrderedListicle,
    PlateCrossheadSubsection,
    PlateCrossheadUnorderedListicle,
    UnityCrosshead,
} from './elements/Crosshead';
import { PlateCustomJson, UnityCustomJson } from './elements/CustomJson';
import {
    PlateDynamicTeaser,
    PlateDynamicTeaserTitle,
    UnityDynamicTeaser,
} from './elements/DynamicTeaser';
import { PlateEmbeddedContent, UnityEmbeddedContent } from './elements/EmbeddedContent';
import { PlateFacebook, UnityFacebook } from './elements/Facebook';
import { PlateFooter, UnityFooter } from './elements/Footer';
import { PlateFrontendComponent, UnityFrontendComponent } from './elements/FrontendComponent';
import { PlateImage, UnityImage } from './elements/Image';
import {
    PlateInfobox,
    PlateInfoboxContent,
    PlateInfoboxTitle,
    UnityInfobox,
} from './elements/Infobox';
import { PlateInstagram, UnityInstagram } from './elements/Instagram';
import {
    PlateInterviewSegment,
    PlateInterviewSegmentAnswer,
    PlateInterviewSegmentQuestion,
    UnityInterviewSegment,
} from './elements/InterviewSegment';
import { PlateLead, UnityLead } from './elements/Lead';
import { PlateLink, UnityLink } from './elements/Link';
import {
    ListItemContent,
    PlateList,
    PlateListItem,
    PlateOrderedList,
    PlateUnorderedList,
    UnityOrderedList,
    UnityUnorderedList,
} from './elements/List';
import { PlateParagraph, UnityParagraph } from './elements/Paragraph';
import { PlatePoll, PlatePollAnswer, PlatePollQuestion, UnityPoll } from './elements/Poll';
import { PlateQuote, PlateQuoteCaption, PlateQuoteText, UnityQuote } from './elements/Quote';
import { PlateRatingPoll, UnityRatingPoll } from './elements/RatingPoll';
import { PlateSeparator, UnitySeparator } from './elements/Separator';
import {
    PlateCsSlideshow,
    PlateSlideshow,
    UnityCsSlideshow,
    UnitySlideshow,
} from './elements/Slideshow';
import {
    PlateEmbeddedSnippet,
    PlateSnippet,
    UnityEmbeddedSnippet,
    UnitySnippet,
} from './elements/Snippet';
import { PlateSummaryList, PlateSummaryListSummary, UnitySummaryList } from './elements/Summary';
import { PlateThreads, UnityThreads } from './elements/Threads';
import { PlateTiktok, UnityTiktok } from './elements/Tiktok';
import { PlateTitle, UnityTitle } from './elements/Title';
import { PlateTitleHeader, UnityTitleHeader } from './elements/TitleHeader';
import { PlateTwitter, UnityTwitter } from './elements/Twitter';
import { PlateVideoCms, UnityVideoCms } from './elements/VideoCms';
import { PlateYoutube, UnityYoutube } from './elements/Youtube';
import { PlateZattoo, UnityZattoo } from './elements/Zattoo';

export const PlateInlineElement = z.discriminatedUnion('type', [PlateLink], {
    unionFallback: true,
});
export type InlineElement = z.infer<typeof PlateInlineElement>;

export const PlateTextElement = z.discriminatedUnion(
    'type',
    [
        PlateParagraph,
        PlateCrossheadDefault,
        PlateCrossheadSubsection,
        PlateCrossheadOrderedListicle,
        PlateCrossheadUnorderedListicle,
        PlateTitleHeader,
        PlateTitle,
        PlateLead,
        PlateFooter,
    ],
    { unionFallback: true },
);
export type TextElement = z.infer<typeof PlateTextElement>;

export const TEXT_HEADING_ELEMENT_TYPES = getElementTypes([
    PlateTitleHeader,
    PlateTitle,
    PlateLead,
]);

export const PlateTextBlockElement = z.discriminatedUnion('type', [PlateTextElement, PlateList], {
    unionFallback: true,
});
export type TextBlockElement = z.infer<typeof PlateTextBlockElement>;

export const TEXT_BLOCK_ELEMENT_TYPES = getElementTypes([PlateTextBlockElement]);

const PlateEmbedElement = z.discriminatedUnion(
    'type',
    [
        PlateBluesky,
        PlateCsSlideshow,
        PlateDynamicTeaser,
        PlateEmbeddedContent,
        PlateFacebook,
        PlateImage,
        PlateInfobox,
        PlateInstagram,
        PlateInterviewSegment,
        PlatePoll,
        PlateRatingPoll,
        PlateQuote,
        PlateSlideshow,
        PlateSummaryList,
        PlateThreads,
        PlateTiktok,
        PlateTwitter,
        PlateYoutube,
        PlateZattoo,
        PlateFrontendComponent,
        PlateCustomJson,
        PlateEmbeddedSnippet,
        PlateSnippet,
        PlateVideoCms,
    ],
    { unionFallback: true },
);

export const PlateBlockElement = z.discriminatedUnion(
    'type',
    [
        PlateTextElement,
        PlateEmbedElement,
        PlateOrderedList,
        PlateUnorderedList,
        PlateListItem,
        ListItemContent,
        PlatePollQuestion,
        PlatePollAnswer,
        PlateQuoteText,
        PlateQuoteCaption,
        PlateSummaryListSummary,
        PlateSeparator,
        PlateInfoboxTitle,
        PlateInfoboxContent,
        PlateInterviewSegmentQuestion,
        PlateInterviewSegmentAnswer,
        PlateDynamicTeaserTitle,
        Caption,
        CaptionTitle,
        Credit,
    ],
    { unionFallback: true },
);
export type BlockElement = z.infer<typeof PlateBlockElement>;

export const BLOCK_ELEMENT_TYPES = getElementTypes([PlateBlockElement]);

export const NON_LISTABLE_ELEMENT_TYPES = getElementTypes([
    Caption,
    Credit,
    PlatePollQuestion,
    PlatePollAnswer,
    PlateInterviewSegmentQuestion,
    PlateInterviewSegmentAnswer,
    PlateQuoteText,
    PlateQuoteCaption,
    PlateInfoboxTitle,
    PlateDynamicTeaserTitle,
    PlateSummaryListSummary,
]);

// We want to keep Zod as the source of the type
const PlateElement = z.discriminatedUnion('type', [PlateBlockElement, PlateInlineElement], {
    unionFallback: true,
});
export type Element = z.infer<typeof PlateElement>;

// We want to keep Zod as the source of the type
export const RootPlateElement = z.discriminatedUnion(
    'type',
    [PlateTextElement, PlateEmbedElement, PlateOrderedList, PlateUnorderedList, PlateSeparator],
    {
        unionFallback: true,
    },
);
export type RootElement = z.infer<typeof RootPlateElement>;

export const UnityElement = z.discriminatedUnion('type', [
    UnityBluesky,
    UnityCrosshead,
    UnityDynamicTeaser,
    UnityEmbeddedContent,
    UnityFacebook,
    UnityFooter,
    UnityFrontendComponent,
    UnityCustomJson,
    UnityImage,
    UnityInfobox,
    UnityInstagram,
    UnityInterviewSegment,
    UnityLead,
    UnityLink,
    UnityOrderedList,
    UnityUnorderedList,
    UnityParagraph,
    UnityPoll,
    UnityQuote,
    UnityRatingPoll,
    UnitySeparator,
    UnitySlideshow,
    UnityCsSlideshow,
    UnitySnippet,
    UnityEmbeddedSnippet,
    UnitySummaryList,
    UnityThreads,
    UnityTiktok,
    UnityTitle,
    UnityTitleHeader,
    UnityTwitter,
    UnityVideoCms,
    UnityYoutube,
    UnityZattoo,
]);

export type UnityElement = z.infer<typeof UnityElement>;
